/**
 * The strip across the top of the desktop window.
 *
 * The window is frameless (electron/main.js, frame: false), so this is its
 * title bar: it drags the window, and it carries minimize, maximize and close.
 * When the sidebar replaced the old top bar on desktop, those went with it -
 * the installed app had no way to move, maximise or close its own window
 * except from the taskbar. This puts them back.
 *
 * Search sits here the way the Home reference draws it, beside Create party
 * and the bell. Staff get the console (with its sections on hover) and a
 * direct way to the server status page; nobody else sees either.
 *
 * Phones keep TopBar and the tab bar; this is desktop only.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';
import { bridge, isDesktop } from '@/lib/bridge';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { UpdatePill } from './UpdatePill';
import { NotificationBell } from './NotificationPeek';
import { BackButton, StaffButton, ToolButton, WinButton } from './TopBar';

export function AppTitleBar({ route, onNavigate, onSearch, onCreate }: {
  route: string;
  onNavigate: (id: string) => void;
  onSearch: () => void;
  onCreate: () => void;
}) {
  const t = useT();
  const me = useSession((s) => s.me);
  const [maximized, setMaximized] = useState(false);
  const admin = me?.role === 'admin';
  const shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

  useEffect(() => {
    const api = bridge();
    if (!api?.onWindowState) return;
    return api.onWindowState((s) => setMaximized(!!s.maximized));
  }, []);

  return (
    <header
      /* The whole strip drags; every control opts back out with no-drag, or
         pressing it would move the window instead. Double-click maximises,
         as a native title bar does. */
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('.no-drag')) return;
        if (isDesktop()) void bridge()?.window('maximize');
      }}
      data-chrome="nav"
      className="drag relative z-40 flex h-[64px] flex-none items-center gap-2 border-b border-line-soft bg-surface-0 pr-2 pl-3"
    >
      <BackButton />

      <div className="min-w-[12px] flex-1" />

      {/* Only present when there is something to install. */}
      <div className="no-drag flex-none"><UpdatePill /></div>

      {/* Search on the right, just before the icons - as asked. */}
      <button
        type="button"
        onClick={onSearch}
        aria-label={'Search games, friends or parties, ' + shortcut}
        className={cn(
          'no-drag flex h-[44px] w-[min(480px,42vw)] min-w-[200px] items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-1 px-3.5 text-left',
          'text-[14.5px] text-mute outline-none transition-[border-color,box-shadow,color] duration-[var(--t-hover)] ease-[var(--ease)]',
          'hover:border-line hover:text-dim focus-visible:border-accent focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]'
        )}
      >
        <Icon name="search" size={18} className="flex-none" />
        <span className="min-w-0 flex-1 truncate">{t('top.search')}</span>
        <kbd className="flex-none rounded-[calc(6px*var(--radius-mul,1))] border border-line-soft bg-surface-2 px-1.5 py-px font-sans text-[11.5px] text-mute">{shortcut}</kbd>
      </button>

      {/* The icons: badges for everyone, then staff tools, then the bell. */}
      <div className="no-drag flex flex-none items-center gap-2">
        <ToolButton item={{ id: 'badges', label: 'Badges', icon: 'badges' }} on={route === 'badges'} onClick={() => onNavigate('badges')} />
        <ToolButton item={{ id: 'leaderboard', label: 'Leaderboards', icon: 'trophy' }} on={route === 'leaderboard'} onClick={() => onNavigate('leaderboard')} />
      </div>

      {/*
        Both admin only, and both go where they say they go.

        Server status used to point at "staff/server", which is not a route -
        so the one button for it in the desktop app landed on the staff
        console or on "Not for you", depending on who pressed it. The page
        has been there the whole time with no working way in.
      */}
      {admin ? (
        <div className="no-drag flex flex-none items-center gap-2">
          <StaffButton item={{ id: 'staff', label: 'Staff console', icon: 'shieldNav' }} on={route === 'staff'} onClick={() => onNavigate('staff')} />
          <ToolButton item={{ id: 'status', label: 'Server status', icon: 'bolt' }} on={route === 'status'} onClick={() => onNavigate('status')} />
        </div>
      ) : null}

      <button
        type="button"
        onClick={onCreate}
        className="no-drag flex h-[44px] flex-none items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-5 text-[15px] font-bold text-accent-fg outline-none transition-[filter,box-shadow] duration-[var(--t-hover)] hover:bg-accent-hi focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
      >
        <Icon name="plus" size={17} />
        {t('top.createParty')}
      </button>

      <div className="no-drag flex-none"><NotificationBell /></div>

      {isDesktop() ? (
        <div className="no-drag ml-1 flex flex-none items-center border-l border-line-soft pl-1.5">
          <WinButton label="Minimize" onClick={() => void bridge()?.window('minimize')}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.4" /></svg>
          </WinButton>
          <WinButton label={maximized ? 'Restore' : 'Maximize'} onClick={() => void bridge()?.window('maximize')}>
            {maximized ? (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
                <rect x="2" y="3.6" width="6.4" height="6.4" rx="1.1" /><path d="M4 3.6V2.8A.8.8 0 0 1 4.8 2h4.4a.8.8 0 0 1 .8.8v4.4a.8.8 0 0 1-.8.8h-.8" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                <rect x="2.4" y="2.4" width="7.2" height="7.2" rx="1.3" />
              </svg>
            )}
          </WinButton>
          <WinButton label="Close" danger onClick={() => void bridge()?.window('close')}>
            <svg width="12" height="12" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
              <path d="M3 3l6 6M9 3l-6 6" />
            </svg>
          </WinButton>
        </div>
      ) : null}
    </header>
  );
}
